import moment from 'moment-timezone';

import {
  ChatterContextInput,
  ChatterMessageSegment,
  ChatterMessageSegmentInput,
  ChatterWatchersInput,
  SalesforceChatterComment,
  SalesforceChatterFeedItem
} from 'types/SalesforceChatter.types';
import { formatDateWithTimeZone } from 'utils/dateUtils';

import {
  CHATTER_DATE_FORMAT,
  CRM_CCID_REGEX,
  CRM_SENT_LINE_PREFIX,
  HTML_TAG_TO_MARKUP,
  MARKUP_TO_HTML_TAG,
  MENTION_REGEX,
  PASTE_BLOCK_TAGS,
  PASTE_HEADING_TAGS,
  PASTE_INLINE_FORMAT_TAGS,
  PASTE_SKIP_TAGS,
  PASTE_TAG_NORMALIZE,
  TREATMENT_MONITOR_BASE
} from './constants';
import { ChatterRecordContext } from './types';

export const stripCrmSentLineFromText = (text: string): string =>
  text
    .split('\n')
    .filter((line) => !line.trimStart().startsWith(CRM_SENT_LINE_PREFIX))
    .join('\n');

export const stripCrmSentLineFromSegments = (
  segments: ChatterMessageSegment[]
): ChatterMessageSegment[] =>
  segments.flatMap((segment) => {
    if (segment.type !== 'Text' || !segment.text) {
      return [segment];
    }

    const cleanedText = stripCrmSentLineFromText(segment.text);
    if (!cleanedText) {
      return [];
    }

    return [{ ...segment, text: cleanedText }];
  });

export const formatChatterDate = (value?: string | null) =>
  value
    ? formatDateWithTimeZone(value, moment.tz.guess(), CHATTER_DATE_FORMAT)
    : '';

export const getChatterFileDownloadUrl = (fileId: string): string =>
  `${TREATMENT_MONITOR_BASE}/salesforce/chatter/files/${encodeURIComponent(
    fileId
  )}/content`;

export const getNoteFileDownloadUrl = (fileId: string): string =>
  `${TREATMENT_MONITOR_BASE}/salesforce/notes/files/${encodeURIComponent(
    fileId
  )}/content`;

export const extractFileIdFromUrl = (url: string): string | null => {
  const match = url.match(/\/files\/([a-zA-Z0-9]{15,18})\//);
  return match?.[1] ?? null;
};

export const formatFileSize = (size?: string | null): string => {
  if (!size) return '';
  const bytes = parseInt(size, 10);
  if (isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const unescapeHtml = (text: string): string =>
  text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const encodeMention = (name: string, id: string): string =>
  `@${name}<CCID:${id}>`;

export const extractMentionIdsFromElement = (
  container: HTMLElement
): string[] => {
  const ids = new Set<string>();
  container.querySelectorAll('[data-mention-id]').forEach((el) => {
    const id = el.getAttribute('data-mention-id')?.trim();
    if (id) ids.add(id);
  });
  return Array.from(ids);
};

export const extractCcIdsFromText = (text: string): string[] => {
  const ids: string[] = [];
  for (const line of text.split('\n')) {
    if (!line.trimStart().startsWith(CRM_SENT_LINE_PREFIX)) continue;
    const regex = new RegExp(CRM_CCID_REGEX.source, 'gi');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(line)) !== null) {
      const id = match[1]?.trim();
      if (id) ids.push(id);
    }
  }
  return ids;
};

export const extractCcIdsFromChatterContent = (
  body?: string | null,
  messageSegments?: ChatterMessageSegment[] | null
): string[] => {
  const ids = new Set<string>();
  if (body) {
    extractCcIdsFromText(body).forEach((id) => ids.add(id));
  }
  messageSegments?.forEach((segment) => {
    if (segment.type === 'Text' && segment.text) {
      extractCcIdsFromText(segment.text).forEach((id) => ids.add(id));
    }
  });
  return Array.from(ids);
};

export const collectThreadReplyingToIds = (
  feedItem: SalesforceChatterFeedItem,
  comments: SalesforceChatterComment[],
  excludeUserId?: string | null
): string[] => {
  const ids = new Set<string>(
    extractCcIdsFromChatterContent(feedItem.body, feedItem.messageSegments)
  );
  comments.forEach((comment) => {
    extractCcIdsFromChatterContent(
      comment.body,
      comment.messageSegments
    ).forEach((id) => ids.add(id));
  });
  if (excludeUserId) {
    ids.delete(excludeUserId);
  }
  return Array.from(ids);
};

export const buildChatterWatchers = (
  mentionIds: string[],
  replyingTo?: string[]
): ChatterWatchersInput | undefined => {
  const mentions = Array.from(new Set(mentionIds.filter(Boolean)));
  const replyIds = replyingTo
    ? Array.from(new Set(replyingTo.filter(Boolean)))
    : [];

  if (mentions.length === 0 && replyIds.length === 0) {
    return undefined;
  }

  return {
    ...(mentions.length > 0 ? { mentions } : {}),
    ...(replyIds.length > 0 ? { replyingTo: replyIds } : {})
  };
};

export const buildChatterContext = (
  recordContext: ChatterRecordContext,
  mentionIds: string[],
  replyingTo?: string[]
): ChatterContextInput => {
  const watchers = buildChatterWatchers(mentionIds, replyingTo);

  return {
    recordId: recordContext.recordId,
    recordName: recordContext.recordName,
    recordType: recordContext.recordType,
    ...(watchers ? { watchers } : {})
  };
};

export const parseMentions = (
  text: string
): (string | { name: string; id: string })[] => {
  const parts: (string | { name: string; id: string })[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(MENTION_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push({ name: match[1], id: match[2] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

export const chatterBodyToPlainText = (body?: string | null): string => {
  if (!body) return '';

  const cleaned = stripCrmSentLineFromText(unescapeHtml(body)).trim();
  if (!cleaned) return '';

  return parseMentions(cleaned)
    .map((part) => (typeof part === 'string' ? part : `@${part.name}`))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
};

export const htmlToMessageSegments = (
  container: HTMLElement
): ChatterMessageSegmentInput[] => {
  const segments: ChatterMessageSegmentInput[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text) {
        segments.push({ type: 'Text', text });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'br') {
      segments.push({ type: 'Text', text: '\n' });
      return;
    }

    if (tag === 'img') {
      const fileId = el.getAttribute('data-file-id');
      if (fileId) {
        segments.push({
          type: 'InlineImage',
          fileId,
          altText: el.getAttribute('alt') || undefined
        });
      }
      return;
    }

    if (tag === 'span' && el.hasAttribute('data-mention-id')) {
      const mentionId = el.getAttribute('data-mention-id') || '';
      const mentionName =
        el.getAttribute('data-mention-name') ||
        el.textContent?.replace(/^@/, '') ||
        '';
      const sfId = el.getAttribute('data-sf-id');
      if (sfId) {
        segments.push({
          type: 'Mention',
          recordId: sfId
        });
      } else {
        segments.push({
          type: 'Text',
          text: encodeMention(mentionName, mentionId)
        });
      }
      return;
    }

    const markupType = HTML_TAG_TO_MARKUP[tag];
    if (markupType) {
      segments.push({ type: 'MarkupBegin', markupType });
      Array.from(el.childNodes).forEach(walk);
      segments.push({ type: 'MarkupEnd', markupType });
    } else {
      Array.from(el.childNodes).forEach(walk);
    }
  };

  Array.from(container.childNodes).forEach(walk);
  return segments;
};

export const mentionTextToHtml = (text: string): string => {
  const regex = new RegExp(MENTION_REGEX.source, 'g');
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let result = '';
  while ((match = regex.exec(text)) !== null) {
    result += escapeHtml(text.slice(lastIdx, match.index));
    const name = match[1];
    const id = match[2];
    result += `<span data-mention-id="${escapeHtml(
      id
    )}" data-mention-name="${escapeHtml(
      name
    )}" contenteditable="false" class="mention-tag">@${escapeHtml(
      name
    )}</span>`;
    lastIdx = regex.lastIndex;
  }
  result += escapeHtml(text.slice(lastIdx));
  return result;
};

export const segmentsToHtml = (segments: ChatterMessageSegment[]): string => {
  let html = '';
  for (const seg of segments) {
    switch (seg.type) {
      case 'MarkupBegin': {
        const tag =
          seg.htmlTag || MARKUP_TO_HTML_TAG[seg.markupType || ''] || 'span';
        html += `<${tag}>`;
        break;
      }
      case 'MarkupEnd': {
        const tag =
          seg.htmlTag || MARKUP_TO_HTML_TAG[seg.markupType || ''] || 'span';
        html += `</${tag}>`;
        break;
      }
      case 'Text': {
        const rawText = seg.text || '';
        const regex = new RegExp(MENTION_REGEX.source, 'g');
        let lastIdx = 0;
        let match: RegExpExecArray | null;
        let result = '';
        while ((match = regex.exec(rawText)) !== null) {
          result += escapeHtml(rawText.slice(lastIdx, match.index)).replace(
            /\n/g,
            '<br>'
          );
          const name = match[1];
          const id = match[2];
          result += `<span data-mention-id="${escapeHtml(
            id
          )}" data-mention-name="${escapeHtml(
            name
          )}" contenteditable="false" class="mention-tag">@${escapeHtml(
            name
          )}</span>`;
          lastIdx = regex.lastIndex;
        }
        result += escapeHtml(rawText.slice(lastIdx)).replace(/\n/g, '<br>');
        html += result;
        break;
      }
      case 'InlineImage': {
        const editorFileId =
          seg.fileId ||
          (seg.imageUrl ? extractFileIdFromUrl(seg.imageUrl) : null);
        if (editorFileId) {
          html += `<img data-file-id="${escapeHtml(
            editorFileId
          )}" alt="${escapeHtml(
            seg.altText || ''
          )}" src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='40'%3E%3Crect width='120' height='40' rx='6' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='55%25' dominant-baseline='middle' text-anchor='middle' font-size='11' fill='%236b7280'%3E📷 Image%3C/text%3E%3C/svg%3E" style="border-radius:6px;cursor:pointer;border:1px solid #d1d5db" />`;
        }
        break;
      }
    }
  }
  return html;
};

// --- Rich-text paste sanitization ---

const PASTE_STYLE_TO_TAG: [RegExp, string][] = [
  [/font-weight\s*:\s*(bold|bolder|[6-9]\d{2})\b/i, 'b'],
  [/font-style\s*:\s*italic/i, 'i'],
  [/text-decoration[^:]*:[^;]*underline/i, 'u'],
  [/text-decoration[^:]*:[^;]*line-through/i, 's']
];

const PASTE_FORMAT_NEGATE: Record<string, RegExp> = {
  b: /font-weight\s*:\s*(normal|[1-4]\d{2})\b/i,
  strong: /font-weight\s*:\s*(normal|[1-4]\d{2})\b/i,
  i: /font-style\s*:\s*normal/i,
  em: /font-style\s*:\s*normal/i,
  u: /text-decoration[^:]*:\s*none/i,
  s: /text-decoration[^:]*:\s*none/i,
  strike: /text-decoration[^:]*:\s*none/i,
  del: /text-decoration[^:]*:\s*none/i
};

const TABLE_CONTAINER_TAGS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'colgroup',
  'col',
  'caption'
]);

const wrapWithStyleFormats = (content: string, style: string): string => {
  if (!style || !content) return content;
  let result = content;
  for (const [pattern, wrapTag] of PASTE_STYLE_TO_TAG) {
    if (pattern.test(style)) {
      result = `<${wrapTag}>${result}</${wrapTag}>`;
    }
  }
  return result;
};

/**
 * Sanitize clipboard HTML down to the formatting subset the editor supports.
 * Handles content from Google Docs, Word, Notion, Slack, web pages, etc.
 */
export const sanitizePastedHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent || '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (PASTE_SKIP_TAGS.has(tag)) return '';
    if (tag === 'br') return '<br>';

    const children = Array.from(el.childNodes).map(processNode).join('');

    if (PASTE_INLINE_FORMAT_TAGS.has(tag)) {
      const style = el.getAttribute('style') || '';
      const negatePattern = PASTE_FORMAT_NEGATE[tag];
      if (negatePattern && style && negatePattern.test(style)) {
        return wrapWithStyleFormats(children, style);
      }
      const normalized = PASTE_TAG_NORMALIZE[tag] || tag;
      return `<${normalized}>${children}</${normalized}>`;
    }

    if (tag === 'ul' || tag === 'ol') return `<${tag}>${children}</${tag}>`;
    if (tag === 'li') return `<li>${children}</li>`;
    if (tag === 'p') return `<p>${children}</p>`;

    if (PASTE_HEADING_TAGS.has(tag)) return `<p><b>${children}</b></p>`;

    if (tag === 'td' || tag === 'th') return children + ' ';
    if (tag === 'tr') return children.trimEnd() + '<br>';
    if (TABLE_CONTAINER_TAGS.has(tag)) return children;

    if (PASTE_BLOCK_TAGS.has(tag)) return `<p>${children}</p>`;

    const style = el.getAttribute('style') || '';
    return wrapWithStyleFormats(children, style);
  };

  return Array.from(doc.body.childNodes).map(processNode).join('');
};
